
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import {
  Task, Lead, PermissionCode, TaskStatus, LeadType, LeadStatus, ProjectsPool,
} from '@/types';
import TaskStatusAlert from '@/components/TaskStatusAlert.vue';
import TaskActions from '@/components/TaskActions.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import {
  currentUserHasPermission,
  getLeadCustomValueDate,
  getLeadCustomValueText,
} from '@/helpers';

moment.locale('it');

@Component({
  components: {
    TaskStatusAlert,
    TaskActions,
    DatePickerDialog,
  },
})
export default class TaskC extends Vue {
  @Prop(Object) readonly task: Task | undefined;
  @Prop({ type: Boolean, default: false }) readonly embedded!: boolean;
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;

  // Il form è valido?
  isFormValid = false

  selectedStatus: 'completed' | 'error' = 'completed'

  datePickerDialog = false
  datePickerDate: string | null = null
  datePickerName: string | null = null

  dataVisitaCommerciale: Date | null = null
  noteVisitaCommerciale: string | null = null

  created() {
    console.log(`${this.constructor.name}: created()`); // eslint-disable-line no-console

    // Inizializza tutti i campi del modulo
    this.refreshTask();
  }

  // Aggiorna i campi con gli eventuali nuovi valori della Lead selezionata
  refreshTask() {
    console.log(`${this.constructor.name}: refreshTask()`); // eslint-disable-line no-console

    this.dataVisitaCommerciale = getLeadCustomValueDate(this.selectedLead, 'dataVisitaCommerciale');
    this.noteVisitaCommerciale = getLeadCustomValueText(this.selectedLead, 'noteVisitaCommerciale');
  }

  // Il Task è valido quando sia il form che i dati extra lo sono
  get valid(): boolean {
    return this.isFormValid && this.isExtraDataValid;
  }

  // I dati extra sono validi?
  get isExtraDataValid(): boolean {
    // Se è impossibile completare il task, i dati extra sono ignorati
    // FIXME: Rimuovere quando questo Task userà il mixin TaskMixin
    if (this.selectedStatus === 'error') return true;

    // In tutti gli altri casi...
    return true;
  }

  // Se la validità del form cambia, la memorizza nello stato di Vuex
  @Watch('valid')
  onValidChange(val: boolean) {
    console.log(`${this.constructor.name}: onValidChange() val =`, val); // eslint-disable-line no-console

    this.$store.dispatch('updateTaskFieldsValidity', { value: val });
  }

  get selectedLead(): Lead | null {
    const childLeads: ProjectsPool = this.$store.getters.currentChildren;

    // Se è stato specificato un Task e questo è associato ad una Lead figlia...
    if (this.task && this.task.relationship === 'father') {
      // ... se la lead figlia non è stata ancora assegnata,
      // non ritorna nulla.
      if ('childLeadId' in this.task === false || !this.task.childLeadId) return null;

      // ... se la lead figlia è stata assegnata,
      // considera i suoi dati.
      return childLeads[this.task.childLeadId];
    }

    // Negli altri casi ritorna la Lead selezionata memorizzata tramite Vuex
    return this.$store.getters.currentLead;
  }

  get selectedTask(): Task | null {
    // Se non è stato specificato un Task nelle Prop,
    // utilizza il Task selezionato memorizzato tramite Vuex.
    return this.task ?? this.$store.getters.selectedTask;
  }

  get loading(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskLoading;
  }

  get updating(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskUpdating;
  }

  // Ritorma true se è in corso l'aggiornamento della Lead mostrata
  get leadUpdating(): boolean { return this.$store.getters.isProjectUpdating; }

  // Tiene sotto controllo leadUpdateing per capire quando eventualmente
  // vanno aggiornati i dati visualizzati.
  @Watch('leadUpdating')
  onLeadUpdatingChange(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onLeadUpdatingChange() val = ${val} oldVal = ${oldVal}`); // eslint-disable-line no-console

    // Se leadUpdating passa da true a folse,
    // la Lead ha completato l'aggiornamento e vanno aggiornati i dati visualizzati.
    if (val === false && oldVal === true) {
      // Aggiorna la Lead con i nuovi valori soltanto se è in visualizzazione readonly
      if (this.readonly) this.refreshTask();
    }
  }

  get leadType(): LeadType | null { return this.selectedLead?.type ?? null; }

  get leadStatus(): LeadStatus | null { return this.selectedLead?.status ?? null; }

  get taskStatus(): TaskStatus { return this.selectedTask?.status ?? null; }

  get form(): Vue & { validate: () => boolean; reset: () => boolean } {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  get appendIcon(): string | null { return this.fieldsDisabled ? 'mdi-lock' : null; }

  get fieldsDisabled(): boolean {
    return (this.task !== null && this.task !== undefined)
      || this.selectedTask?.status !== null
      || this.readonly
      || this.loading
      || this.updating;
  }

  // Mostra le azioni nell'Alert soltanto sulle lead figlie e sui Task senza figlie
  get showAlertActions(): boolean { return this.task?.relationship !== 'father'; }

  // Mostra le azioni
  get showActions(): boolean { return !this.task && !this.readonly && !this.embedded; }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  mDate(date: Date | null | undefined): string | null { return date ? moment(date).format('L') : null; }

  onClickDate(name: string) {
    console.log(`${this.constructor.name}: onClickDate(${name})`); // eslint-disable-line no-console

    if (this.leadStatus !== 'open'
      || this.taskStatus !== null
      || this.readonly) return;

    // Imposta per quale campo verrà utilizzato il DatePicker
    this.datePickerName = name;

    // Imposta l'eventuale data precedentemente selezionata
    this.datePickerDate = this[name]
      ? moment(this[name]).toISOString(true).substring(0, 10)
      : null;

    // Apre il dialog per la selezione della data
    this.datePickerDialog = true;
  }

  onClearDate(name: string) {
    console.log(`${this.constructor.name}: onClearDate(${name})`); // eslint-disable-line no-console

    if (this.leadStatus !== 'open') return;

    const update = {} as { [key: string]: unknown };

    // Cancello la data nel modulo e nel prossimo aggiornamento
    if (name in this) {
      this[name] = null;
      update[name] = null;
    }

    // Modifico eventuali campi collegati
    switch (name) {
      default:
        break;
    }

    // Valido il modulo con le nuove informazioni inserite
    this.onValidate('completed');

    this.$store.dispatch('updateTaskFields', update);
  }

  onSelectDate(date: string | null) {
    console.log(`${this.constructor.name}: onSelectDate() date =`, date); // eslint-disable-line no-console

    if (!this.datePickerName) return;

    // Chiudo il dialog per la selezione della data
    this.datePickerDialog = false;

    const update = {} as { [key: string]: unknown };

    // Trasforma in un valore di tipo Date prima di restituirlo
    const selectedDate = date
      ? moment(date, 'YYYY-MM-DD').toDate()
      : null;

    // Inserisco la data nel modulo e nell'aggiornamento
    if (this.datePickerName in this) {
      this[this.datePickerName] = selectedDate;
      update[this.datePickerName] = this[this.datePickerName];
    }

    // Modifico eventuali campi collegati
    switch (this.datePickerName) {
      default:
        break;
    }

    // Valido il modulo con le nuove informazioni inserite
    this.onValidate('completed');

    this.$store.dispatch('updateTaskFields', update);
  }

  onFieldUpdated(field: string) {
    console.log(`${this.constructor.name}: onFieldUpdated(${field})`); // eslint-disable-line no-console

    const update = {} as { [key: string]: unknown };

    // Aggiorna sempre il campo specificato
    update[field] = this[field] ?? '';

    // Aggiorna eventuali campi collegati, eccezioni, ecc.
    switch (field) {
      default:
        break;
    }

    this.$store.dispatch('updateTaskFields', update);
  }

  // Effetta la validazione del modulo
  async onValidate(status: 'completed' | 'error') {
    console.log(`${this.constructor.name}: onValidate(${status})`); // eslint-disable-line no-console

    // Sceglie se validare il modulo per un completamento o per un fallimento
    this.selectedStatus = status;

    // Avvia l'effettiva validazione
    const form = this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };

    if (form) {
      const result = form.validate();
      console.log(`${this.constructor.name}: onValidate() form.validate() =`, result); // eslint-disable-line no-console
    } else {
      console.error(`${this.constructor.name}: onValidate() form =`, form); // eslint-disable-line no-console
      console.error(`${this.constructor.name}: onValidate() this.$refs =`, this.$refs); // eslint-disable-line no-console
    }

    return Promise.resolve();
  }

  onEdit() {
    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    this.$emit('edit');
  }

  onReset() {
    console.log(`${this.constructor.name}: onReset()`); // eslint-disable-line no-console

    this.$emit('reset');
  }
}
